import { sectionBorderColor } from "@common/components/dotLegalSection/DotLegalSection";
import { Accordion, AccordionDetails, AccordionSummary, Badge, Box, styled, useTheme } from "@mui/material";
import React from "react";
import {
    DotLegalChip,
    DotLegalDatePicker,
    DotLegalHeader,
    DotLegalLink,
    DotLegalMultiSelect,
    DotLegalSelect,
    DotLegalTextField,
    DotLegalTooltip,
} from "@dotlegal/dotlegal-ui-components";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { useMasterDataSection } from "./MasterDataSection.hooks";
import { useTranslation } from "../../../localization/useTranslation";
import { ApprovalOption, TaskRawStateEnum } from "../../../annualWheel/AnnualWheel.types";
import { TrackingEvent, useTrackAIEvent } from "../../../processingActivity/hooks/useTracking";
import { useDotLegalFileUploadDataMapping } from "../../../common/components/dotLegalFileUploadTable/DotLegalFileUploadTable.hooks";
import { AnnualWheelPriority } from "../../../annualWheel/annualWheelDialog/AnnualWheelDialog.types";
import { TaskMasterDataStaticDataViewModel } from "./MasterDataSection.types";
import DotLegalWarningTriangle from "../../../common/components/dotLegalWarningTriangle/DotLegalWarningTriangle";
import { getTaskColor } from "../../task/Task.styles";
import { useRiskIndicator } from "../../../riskAssessment/riskIndicator/RiskIndicator.hooks";
import { isNullOrWhitespace, setFirstLetterToLowerCase } from "../../../common/stringOperations";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider";
import { TagRelationType } from "../../../common/components/tagsMultiSelect/TagsMultiSelect.types.ts";
import PlanInputFieldWrapper from "../../../plan/planInputFieldWrapper/PlanInputFieldWrapper";
import { PurpleLockIcon } from "../../../common/icons";
import DotLegalCollapse from "../../../common/components/dotLegalCollapse/DotLegalCollapse";
import TagsMultiSelectStandAlone from "../../../common/components/tagsMultiSelect/tagsMultiSelectAutoUpdater/TagsMultiSelectAutoUpdater.tsx";
import MappedFrameworkLink from "../../../common/components/MappedFrameworkLink.tsx";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import { IdNamePair } from "../../../customLists/customListView/CustomListView.types.ts";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export interface MasterDataSectionProps {
    taskId?: string;
    masterData?: TaskMasterDataStaticDataViewModel;
    isLoading: boolean;
    passIsDoneLoading?: (newValue: boolean) => void;
    refetchTask: () => void;
    isPdf?: boolean;
    isSingleColumn?: boolean;
    /** The activity this task came from, when the user may open it. Left out when they may not. */
    onOpenActivity?: () => void;
}

// What a value in this section is drawn at. The contract row gets it for free by sitting in an accordion
// summary, which is a button and so carries the browser's button font size; a row that is not in one has
// to name it, or its value comes out larger than the rows around it.
const detailsValueFontSize = 13.3333;
// Below this width the longest labels leave too little room beside them, so each label is stacked above its value.
const stackedLayoutQuery = "@container (max-width: 360px)";
// The height of a standard select, the tallest single-line value. Holding every row to it keeps the text evenly
// spaced whether a value renders as an input, a text field or plain text. Stacked, the label only needs its own
// line, and the space that separates one row from the next moves below the value.
const detailsLabelCellStyle = { minHeight: 34, [stackedLayoutQuery]: { minHeight: 0 } };
const detailsValueCellStyle = { minWidth: 0, [stackedLayoutQuery]: { marginBottom: 1 } };

export const SelectWrapper = styled("div")({
    "& .MuiBox-root:first-child": {
        marginTop: 0,
        marginBottom: 0,
    },
});

function MasterDataSection(props: MasterDataSectionProps) {
    const trackEvent = useTrackAIEvent();
    const theme = useTheme();
    const urlProvider = useUrlProvider();

    const { permissions } = useUserContext();
    const { platformFeatures } = usePlanContext();
    const { translateString, getDateTimePickerLanguage, translateAnnualWheelActivityGeneralTypeEnum, translateDate } = useTranslation();
    const { addTaskToCalendar } = useDotLegalFileUploadDataMapping();
    const {
        options,
        completedAllowed,
        businessAreaData,
        assigneesData,
        responsiblesData,
        allUsers,
        complianceAreas,
        isSectionLoading,
        showAddToCalenderLink,
        priorityOptions,
        data,
        masterDataUpdater,
        groupEntities,
        expandDetails,
        setExpandDetails,
    } = useMasterDataSection(props);
    const { getRiskIcon } = useRiskIndicator();

    const isAdHocTask = props.masterData?.isAdHocTask;

    const convertDate = () => {
        return new Date(props.masterData!.deadline);
    };

    const getValue = (value: string) => {
        return <div>{value}</div>;
    };

    const getLoadingBox = () => {
        return (
            <SelectWrapper>
                <DotLegalSelect
                    disabled={!permissions.taskPermissions.edit}
                    options={[]}
                    selectedItem={undefined}
                    disableClearable
                    onChange={() => {}}
                    placeholder={""}
                    variant={"standard"}
                    label={"Loading..."}
                    noOptionsLabel={"Loading..."}
                    isLoading={isSectionLoading}
                />
            </SelectWrapper>
        );
    };

    const getBagdeColorForTaskState = (id: string) => {
        switch (id) {
            case "1":
                return getTaskColor(TaskRawStateEnum.Ready, theme);
            case "2":
                return getTaskColor(TaskRawStateEnum.InProgress, theme);
            case "5":
                return getTaskColor(TaskRawStateEnum.ReadyForApproval, theme);
            default:
                return getTaskColor(TaskRawStateEnum.Completed, theme);
        }
    };

    const getAddToOutlook = () => {
        const content = (
            <Box
                sx={{
                    "& .MuiTypography-root:hover": {
                        cursor: platformFeatures.addTasksToOutlook ? "pointer" : "default",
                        textDecoration: platformFeatures.addTasksToOutlook ? "underline" : "none",
                    },
                }}
            >
                <DotLegalHeader
                    marginBottom={0}
                    headerStyle={"extraSmall"}
                    color="secondary"
                    fontWeight={400}
                    onClick={() => {
                        if (platformFeatures.addTasksToOutlook) {
                            trackEvent(TrackingEvent.TaskAddedToOutlook, { task: data!.id });
                            addTaskToCalendar(data!.id, props.masterData!.name);
                        }
                    }}
                >
                    {translateString("addToOutLook")}
                </DotLegalHeader>
            </Box>
        );

        if (platformFeatures.addTasksToOutlook) {
            return content;
        }

        return (
            <DotLegalTooltip text={translateString("upgradePrivacyPlanToday")}>
                <span>{content}</span>
            </DotLegalTooltip>
        );
    };

    function canEditAdHocTaskMasterData() {
        if (props.masterData?.isAdHocTask) {
            return permissions.taskPermissions.edit;
        }

        return false;
    }

    function hasEditAccess() {
        if (props.masterData?.isAdHocTask) {
            return permissions.taskPermissions.edit;
        }

        return permissions.canManageAnnualWheelActivityTaskMasterData;
    }

    // An annual wheel task cannot move past the activity's end date; without one there is no upper bound.
    function getDeadlineMaxDate() {
        if (isAdHocTask) {
            return new Date(new Date().getFullYear() + 1, 11, 31);
        }

        const activityEndDate = props.masterData?.deadlineMaxDate;
        return activityEndDate ? new Date(activityEndDate) : undefined;
    }

    const getReadOnlyResponsible = () => {
        if (isSectionLoading) return getLoadingBox();

        const responsible = responsiblesData?.find((x) => x.id === data?.responsible);
        return (
            <DotLegalHeader marginBottom={0} color={responsible ? "primary" : "lightgrey"} fontWeight={400} headerStyle={"small"}>
                {responsible ? responsible.name : translateString("noResponsible")}
            </DotLegalHeader>
        );
    };

    const getEmptyValue = (text: string) => {
        return (
            <DotLegalHeader marginBottom={0} color={"lightgrey"} fontWeight={400} headerStyle={"small"}>
                {text}
            </DotLegalHeader>
        );
    };

    const getReadOnlyPriority = () => {
        if (isSectionLoading) return getLoadingBox();

        const name = data?.priority ? translateString(setFirstLetterToLowerCase(AnnualWheelPriority[data!.priority].toString())) : "";
        return (
            <DotLegalHeader marginBottom={0} color={"primary"} fontWeight={400} headerStyle={"small"}>
                <Box sx={{ "& svg": { marginRight: "12px" } }}>
                    {getRiskIcon(Number(data?.priority))}
                    {name}
                </Box>
            </DotLegalHeader>
        );
    };

    const getResponsibleLockedTooltip = () => {
        if (data?.approval === ApprovalOption.ApprovalNotRequiredResponsibleGetNotified) {
            return translateString("responsibleLockedRequiredForCompletionNotification");
        } else if (data?.approval === ApprovalOption.ApprovalRequiredByResponsible) {
            return translateString("responsibleLockedRequiredForCompletion");
        } else return "";
    };

    function getDeadlineTooltip() {
        if (isAdHocTask || !hasEditAccess()) {
            return "";
        }

        return translateString("annualWheelTaskDeadlineTooltip");
    }

    function getApprovalLockedTooltip() {
        if (data?.responsible) {
            return "";
        }

        return translateString("responsibleRequiredForApprovalTooltip");
    }

    function getControlsToolTip(controls: Array<IdNamePair>) {
        return (
            <React.Fragment>
                {controls.map((x, index) => (
                    <Box sx={{ marginBottom: 0.5, fontSize: 12 }} key={x.id}>
                        {permissions.frameworkPermissions.read ? (
                            <DotLegalLink linkColor="primary" to={urlProvider.getFrameworkControlOverviewUrl(x.id)}>
                                {x.name}
                            </DotLegalLink>
                        ) : (
                            x.name
                        )}
                    </Box>
                ))}
            </React.Fragment>
        );
    }

    function getContractPartiesTooltip(contractParties: Array<IdNamePair>) {
        return (
            <React.Fragment>
                {contractParties.map((x, index) => (
                    <Box sx={{ marginBottom: 0.5, fontSize: 12 }} key={x.id}>
                        {permissions.frameworkPermissions.read ? (
                            <DotLegalLink linkColor="primary" to={urlProvider.getLegalEntityUrl(x.id)}>
                                {x.name}
                            </DotLegalLink>
                        ) : (
                            x.name
                        )}
                    </Box>
                ))}
            </React.Fragment>
        );
    }

    function getAccordion(summery: React.ReactNode, detailsContent: React.ReactNode, hideExpand: boolean = false, key?: string) {
        return (
            <Accordion
                key={key}
                defaultExpanded
                slotProps={{
                    transition: {
                        timeout: 0,
                    },
                }}
                sx={(theme) => ({
                    boxShadow: "none",
                    "&:before": {
                        display: "none",
                    },
                    margin: "0 !important",
                    padding: 0,
                    "& .MuiAccordionSummary-root": {
                        fontFamily: theme.typography.fontFamily,
                    },
                })}
            >
                <AccordionSummary
                    sx={{
                        padding: 0,
                        margin: 0,
                        minHeight: 0,
                        "&.Mui-expanded": {
                            minHeight: 0,
                        },
                        ".MuiAccordionSummary-content": {
                            margin: 0,
                        },
                        ".MuiAccordionSummary-content.Mui-expanded": {
                            margin: 0,
                        },
                    }}
                    expandIcon={hideExpand ? undefined : <ExpandMoreIcon />}
                >
                    <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between" }}>{summery}</Box>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0, margin: 0 }}>{detailsContent}</AccordionDetails>
            </Accordion>
        );
    }

    const responsibleNotChosen = data && (data.responsible === undefined || data.responsible === null || data.responsible === "");
    let assigneesPlaceholder = "";
    if (responsiblesData) {
        const responsiblePerson = data && data.responsible && data.assignees.length === 0 ? responsiblesData.find((x) => x.id === data.responsible) : undefined;
        assigneesPlaceholder = responsiblePerson ? responsiblePerson.name : "";
    }

    const selectedAssignees = assigneesData?.filter((x) => data?.assignees.includes(x.id)) ?? [];
    const selectedBusinessAreas = businessAreaData?.filter((x) => data?.businessAreas.includes(x.id)) ?? [];
    const selectedComplianceAreas = complianceAreas?.filter((x) => data?.complianceAreas.includes(x.id)) ?? [];

    return (
        <>
            <Box sx={{ "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.main } }}>
                <DotLegalSelect
                    disabled={!permissions.taskPermissions.edit}
                    width={props.isSingleColumn ? "100%" : "40%"}
                    disableClearable
                    noMargin
                    label=""
                    options={options}
                    selectedItem={data?.taskState.toString()}
                    onChange={async (state) => await masterDataUpdater.onProgressChange(state)}
                    placeholder=""
                    warningText={completedAllowed}
                    noOptionsLabel={translateString("noOptions")}
                    isLoading={isSectionLoading}
                    renderOptions={(option) => (
                        <Box>
                            <Badge
                                sx={{
                                    marginRight: 1.5,
                                    "& .MuiBadge-badge": {
                                        backgroundColor: getBagdeColorForTaskState(option.id),
                                    },
                                }}
                                variant="dot"
                            />
                            {option.name}
                        </Box>
                    )}
                    inputStartAdornment={
                        data ? (
                            <Badge
                                sx={{
                                    "& .MuiBadge-badge": {
                                        backgroundColor: getTaskColor(data!.taskState, theme),
                                    },
                                }}
                                variant="dot"
                            />
                        ) : undefined
                    }
                    disableSearch
                />
            </Box>
            <DotLegalCollapse
                isExpanded={expandDetails}
                onExpandClick={() => {
                    if (expandDetails) {
                        setExpandDetails(false);
                    } else {
                        setExpandDetails(true);
                    }
                }}
                collapseName={translateString("details")}
                persistCollapseName
                leftAlign
                sx={{
                    marginTop: 2,
                    borderWidth: "1px",
                    borderColor: sectionBorderColor,
                    "&.MuiCollapse-root": {
                        backgroundColor: "white",
                    },
                }}
                isCollapseNameBold
                arrowFontSize={28}
            >
                {/* The section's own width decides the layout, not the window's: in a pane folded into the rail it
                    is narrow however wide the screen is. A container query cannot style the container itself, so
                    the grid it switches sits one level in. */}
                <Box sx={{ containerType: "inline-size" }}>
                    <Box
                        sx={(theme) => ({
                            marginTop: 2,
                            "& .MuiInputBase-root": {
                                marginTop: 0,
                            },
                            "& .MuiInputLabel-root": {
                                display: "none",
                            },
                            marginBottom: props.isPdf ? 2 : 0,
                            // One grid for every row, so the label column is as wide as the longest label and no
                            // label runs into its value. The cells own no margins, so the rows are the same distance
                            // apart whatever a value renders as.
                            display: "grid",
                            gridTemplateColumns: "max-content minmax(0, 1fr)",
                            rowGap: 1,
                            columnGap: 2,
                            [stackedLayoutQuery]: {
                                gridTemplateColumns: "minmax(0, 1fr)",
                                rowGap: 0.5,
                            },
                        })}
                    >
                        <React.Fragment>
                            <Box sx={detailsLabelCellStyle}>
                                <DotLegalHeader marginBottom={0} headerStyle={"small"} toolTip={getDeadlineTooltip()}>
                                    {translateString("deadline")}
                                </DotLegalHeader>
                            </Box>
                            <Box sx={detailsValueCellStyle}>
                                {isSectionLoading ? (
                                    getLoadingBox()
                                ) : (
                                    <>
                                        <Box sx={{ display: "flex", width: "100%", alignItems: "center", gap: 1 }}>
                                            {hasEditAccess() ? (
                                                <Box sx={{ width: "100%" }}>
                                                    <DotLegalDatePicker
                                                        label={""}
                                                        value={data?.deadline ?? null}
                                                        onChange={masterDataUpdater.onDeadlineChange}
                                                        language={getDateTimePickerLanguage()}
                                                        variant="standard"
                                                        noMargin
                                                        disableKeyboardInput
                                                        maxDate={getDeadlineMaxDate()}
                                                    />
                                                </Box>
                                            ) : (
                                                <DotLegalHeader marginBottom={0} headerStyle={"small"} fontWeight={400}>
                                                    {getValue(translateDate(convertDate()))}
                                                </DotLegalHeader>
                                            )}

                                            {props.masterData?.isOverdue && <DotLegalWarningTriangle warningText={translateString("deadlineOverdue")} />}
                                        </Box>
                                        {showAddToCalenderLink() && getAddToOutlook()}
                                    </>
                                )}
                            </Box>
                        </React.Fragment>

                        <React.Fragment>
                            <Box sx={detailsLabelCellStyle}>
                                <DotLegalHeader marginBottom={0} headerStyle={"small"} toolTip={getResponsibleLockedTooltip()}>
                                    {translateString("responsible")}
                                </DotLegalHeader>
                            </Box>
                            <Box sx={detailsValueCellStyle}>
                                {(props.masterData && props.masterData.isProcessingActivityValidation) || !hasEditAccess() ? (
                                    getReadOnlyResponsible()
                                ) : (
                                    <SelectWrapper>
                                        <DotLegalSelect
                                            disableClearable
                                            options={responsiblesData}
                                            isLoading={isSectionLoading}
                                            selectedItem={data?.responsible}
                                            placeholder={translateString("addResponsible")}
                                            variant={"standard"}
                                            label={translateString("responsible")}
                                            onChange={async (responsible) => await masterDataUpdater.onResponsibleChange(responsible)}
                                            noOptionsLabel={translateString("noOptions")}
                                        />
                                    </SelectWrapper>
                                )}
                            </Box>
                        </React.Fragment>

                        {data && data.sharedResponsibles.length > 0 && (
                            <React.Fragment>
                                <Box sx={detailsLabelCellStyle}>
                                    <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                        {translateString("sharedResponsibility")}
                                    </DotLegalHeader>
                                </Box>
                                <Box sx={detailsValueCellStyle}>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1, rowGap: 0.5 }}>
                                        {data.sharedResponsibles.map((id) => {
                                            const user = allUsers?.find((x) => x.id === id);
                                            const name = user ? user.name : id;
                                            return (
                                                <Box key={id}>
                                                    <DotLegalChip isSelected size="small" value={name} />
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </React.Fragment>
                        )}

                        <React.Fragment>
                            <Box sx={detailsLabelCellStyle}>
                                <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                    {translateString("priority")}
                                </DotLegalHeader>
                            </Box>
                            <Box sx={detailsValueCellStyle}>
                                {!hasEditAccess() ? (
                                    getReadOnlyPriority()
                                ) : (
                                    <SelectWrapper>
                                        <DotLegalSelect
                                            options={priorityOptions()}
                                            selectedItem={data?.priority.toString()}
                                            disableClearable
                                            onChange={async (priority) => await masterDataUpdater.onPriorityChange(priority)}
                                            placeholder={""}
                                            variant={"standard"}
                                            label={translateString("priority")}
                                            noOptionsLabel={translateString("noOptions")}
                                            isLoading={isSectionLoading}
                                            renderOptions={(option) => (
                                                <Box sx={{ "& svg": { marginRight: "12px" } }}>
                                                    {getRiskIcon(Number(option.id))}
                                                    {option.name}
                                                </Box>
                                            )}
                                            inputStartAdornment={data ? <Box sx={{ marginLeft: "-12px" }}>{getRiskIcon(data.priority)}</Box> : undefined}
                                        />
                                    </SelectWrapper>
                                )}
                            </Box>
                        </React.Fragment>

                        <React.Fragment>
                            <Box sx={detailsLabelCellStyle}>
                                <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                    {translateString("assign")}
                                </DotLegalHeader>
                            </Box>
                            <Box sx={detailsValueCellStyle}>
                                {isSectionLoading ? (
                                    getLoadingBox()
                                ) : hasEditAccess() ? (
                                    <SelectWrapper>
                                        <DotLegalMultiSelect
                                            disabled={!hasEditAccess()}
                                            options={assigneesData}
                                            chosenOptions={data?.assignees ?? []}
                                            placeholder={responsibleNotChosen ? translateString("assignTo") : assigneesPlaceholder}
                                            variant={"standard"}
                                            label={""}
                                            onChange={async (assigness) => await masterDataUpdater.onAssigneesChange(assigness)}
                                            noOptionsLabel={translateString("noOptions")}
                                        />
                                    </SelectWrapper>
                                ) : selectedAssignees.length === 0 ? (
                                    // Mirrors the editable field's placeholder: with nobody assigned, the responsible carries out the task.
                                    getEmptyValue(isNullOrWhitespace(assigneesPlaceholder) ? translateString("noResponsible") : assigneesPlaceholder)
                                ) : (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1, rowGap: 0.5 }}>
                                        {selectedAssignees.map((x) => {
                                            return (
                                                <Box key={x.id}>
                                                    <DotLegalChip isSelected size="small" key={x.id} value={x.name}></DotLegalChip>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </Box>
                        </React.Fragment>

                        <React.Fragment>
                            <Box sx={detailsLabelCellStyle}>
                                <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                    {translateString("businessAreas")}
                                </DotLegalHeader>
                            </Box>
                            <Box sx={detailsValueCellStyle}>
                                {isSectionLoading ? (
                                    getLoadingBox()
                                ) : (props.masterData && props.masterData.isProcessingActivityValidation) || !hasEditAccess() ? (
                                    selectedBusinessAreas.length === 0 ? (
                                        getEmptyValue(translateString("noBusinessAreas"))
                                    ) : (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1, rowGap: 0.5 }}>
                                            {selectedBusinessAreas.map((x) => {
                                                return (
                                                    <Box key={x.id}>
                                                        <DotLegalChip isSelected size="small" key={x.id} color={x.color} value={x.name}></DotLegalChip>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )
                                ) : (
                                    <SelectWrapper>
                                        <DotLegalMultiSelect
                                            label={translateString("businessAreas")}
                                            options={businessAreaData}
                                            chosenOptions={data?.businessAreas ?? []}
                                            placeholder={translateString("addBusinessArea")}
                                            variant={"standard"}
                                            onChange={async (businessAreas) => await masterDataUpdater.onBusinessAreaChange(businessAreas)}
                                            noOptionsLabel={translateString("noOptions")}
                                            disableItemRemoval={
                                                props.masterData?.isAdHocTask && isNullOrWhitespace(data?.responsible) && data?.businessAreas.length === 1
                                            }
                                            disableClearable
                                        />
                                    </SelectWrapper>
                                )}
                            </Box>
                        </React.Fragment>

                        {props.masterData && !props.masterData.isProcessingActivityValidation && (
                            <React.Fragment>
                                <Box sx={detailsLabelCellStyle}>
                                    <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                        {translateString("complianceAreas")}
                                    </DotLegalHeader>
                                </Box>
                                <Box sx={detailsValueCellStyle}>
                                    {isSectionLoading ? (
                                        getLoadingBox()
                                    ) : canEditAdHocTaskMasterData() ? (
                                        <SelectWrapper>
                                            <DotLegalMultiSelect
                                                label={translateString("complianceAreas")}
                                                options={complianceAreas}
                                                isLoading={isSectionLoading}
                                                chosenOptions={data?.complianceAreas ?? []}
                                                placeholder={translateString("addComplianceArea")}
                                                variant={"standard"}
                                                onChange={async (complianceAreas) => await masterDataUpdater.onComplianceAreaChange(complianceAreas)}
                                                noOptionsLabel={translateString("noOptions")}
                                            />
                                        </SelectWrapper>
                                    ) : selectedComplianceAreas.length === 0 ? (
                                        getEmptyValue(translateString("noComplianceAreas"))
                                    ) : (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1, rowGap: 0.5 }}>
                                            {selectedComplianceAreas.map((x) => {
                                                return (
                                                    <Box key={x.id}>
                                                        <DotLegalChip isSelected size="small" key={x.id} value={x.name}></DotLegalChip>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}
                                </Box>
                            </React.Fragment>
                        )}

                        <React.Fragment>
                            <Box sx={detailsLabelCellStyle}>
                                <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                    ID
                                </DotLegalHeader>
                            </Box>
                            <Box sx={detailsValueCellStyle}>
                                {isSectionLoading ? (
                                    getLoadingBox()
                                ) : canEditAdHocTaskMasterData() ? (
                                    <DotLegalTextField
                                        noMargin
                                        placeholder={translateString("addId")}
                                        label={translateString("addId")}
                                        variant="standard"
                                        value={data?.customId ?? ""}
                                        debounce
                                        onChange={async (value) => await masterDataUpdater.onCustomIdChange(value)}
                                    />
                                ) : isNullOrWhitespace(props.masterData?.customId) ? (
                                    getEmptyValue(translateString("noId"))
                                ) : (
                                    <DotLegalHeader marginBottom={0} headerStyle={"small"} fontWeight={400}>
                                        {props.masterData?.customId}
                                    </DotLegalHeader>
                                )}
                            </Box>
                        </React.Fragment>
                        {/* An ad hoc task has no activity behind it, which is where both the type and the
                            frameworks come from, so the two rows could only ever be empty. And activities on contracts are always type "obligation", so the type row is redundant here and thus hidden */}
                        {!isAdHocTask && !data?.contract && (
                            <React.Fragment>
                                <Box sx={detailsLabelCellStyle}>
                                    <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                        {translateString("type")}
                                    </DotLegalHeader>
                                </Box>
                                <Box sx={detailsValueCellStyle}>
                                    {isSectionLoading ? (
                                        getLoadingBox()
                                    ) : props.masterData?.annualWheelActivityGeneralType ? (
                                        <DotLegalHeader marginBottom={0} headerStyle={"small"} fontWeight={400}>
                                            {translateAnnualWheelActivityGeneralTypeEnum(props.masterData.annualWheelActivityGeneralType.toString())}
                                        </DotLegalHeader>
                                    ) : (
                                        getEmptyValue(translateString("noType"))
                                    )}
                                </Box>
                            </React.Fragment>
                        )}

                        {!data?.contract && !isAdHocTask && (
                            <React.Fragment>
                                <Box sx={detailsLabelCellStyle}>
                                    <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                        {translateString("frameworks")}
                                    </DotLegalHeader>
                                </Box>
                                <Box sx={detailsValueCellStyle}>
                                    {isSectionLoading ? (
                                        getLoadingBox()
                                    ) : data?.frameworks.length === 0 ? (
                                        getEmptyValue(translateString("noFrameworks"))
                                    ) : (
                                        <Box>
                                            {data?.frameworks.map((framework) => {
                                                return getAccordion(
                                                    <React.Fragment>
                                                        <MappedFrameworkLink
                                                            model={{ id: framework.id, name: framework.name, isPlanned: true, goToFrameworkOverview: true }}
                                                        />
                                                        <Box sx={{ whiteSpace: "nowrap" }}>
                                                            <Box
                                                                sx={(theme) => ({
                                                                    fontSize: 13.3333,
                                                                    color: theme.palette.primary.dark,
                                                                })}
                                                            >
                                                                {framework.controls.length === 1
                                                                    ? translateString("oneControl")
                                                                    : translateString("numberOfControls", { numberOfControls: framework.controls.length })}
                                                            </Box>
                                                        </Box>
                                                    </React.Fragment>,
                                                    getControlsToolTip(framework.controls),
                                                    false,
                                                    framework.id
                                                );
                                            })}
                                        </Box>
                                    )}
                                </Box>
                            </React.Fragment>
                        )}

                        {permissions.canAccessTags && (
                            <React.Fragment>
                                <Box sx={detailsLabelCellStyle}>
                                    <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                        {translateString("tags")}
                                    </DotLegalHeader>
                                </Box>

                                <Box sx={detailsValueCellStyle}>
                                    <SelectWrapper>
                                        <TagsMultiSelectStandAlone
                                            entityId={props.taskId!}
                                            entityType={TagRelationType.Task}
                                            isLoading={isSectionLoading}
                                            variant="standard"
                                            disabled={!hasEditAccess()}
                                            displayOnlyAsChips={!hasEditAccess()}
                                            placeHolder={translateString("addTag")}
                                            emptyText={translateString("noTagsEmptyState")}
                                            hideLabel={false}
                                        />
                                    </SelectWrapper>
                                </Box>
                            </React.Fragment>
                        )}

                        <React.Fragment>
                            <Box sx={detailsLabelCellStyle}>
                                <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                    {translateString("notificationDays")}
                                </DotLegalHeader>
                            </Box>
                            <Box sx={detailsValueCellStyle}>
                                {isSectionLoading ? (
                                    getLoadingBox()
                                ) : canEditAdHocTaskMasterData() ? (
                                    <DotLegalTextField
                                        noMargin
                                        placeholder={translateString("addNotification")}
                                        label={translateString("addNotification")}
                                        variant="standard"
                                        value={data?.notificationDays?.toString() ?? ""}
                                        debounce
                                        onChange={async (value) => await masterDataUpdater.onNotificationDaysChange(value)}
                                        onlyNumber="integer"
                                        minNumberValue={1}
                                        maxNumberValue={90}
                                    />
                                ) : data?.notificationDays ? (
                                    <DotLegalHeader marginBottom={0} headerStyle={"small"} fontWeight={400}>
                                        {data.notificationDays}
                                    </DotLegalHeader>
                                ) : (
                                    getEmptyValue(translateString("noNotification"))
                                )}
                            </Box>
                        </React.Fragment>

                        <React.Fragment>
                            <Box sx={detailsLabelCellStyle}>
                                <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                    {translateString("groupCompaniesParenthesis")}
                                </DotLegalHeader>
                            </Box>
                            <Box sx={detailsValueCellStyle}>
                                {isSectionLoading ? (
                                    getLoadingBox()
                                ) : canEditAdHocTaskMasterData() ? (
                                    <SelectWrapper>
                                        <DotLegalSelect
                                            options={groupEntities}
                                            onChange={async (value) => await masterDataUpdater.onGroupEntityChange(value!)}
                                            label={""}
                                            noOptionsLabel={""}
                                            noMargin
                                            variant="standard"
                                            disableClearable
                                            selectedItem={data?.groupEntityId ?? "all"}
                                        />
                                    </SelectWrapper>
                                ) : (
                                    <DotLegalHeader marginBottom={0} headerStyle={"small"} fontWeight={400}>
                                        {props.masterData?.legalEntityName ?? translateString("entireGroup")}
                                    </DotLegalHeader>
                                )}
                            </Box>
                        </React.Fragment>

                        <React.Fragment>
                            <Box sx={detailsLabelCellStyle}>
                                <DotLegalHeader marginBottom={0} headerStyle={"small"} toolTip={getApprovalLockedTooltip()}>
                                    {translateString("approval")}
                                </DotLegalHeader>
                            </Box>
                            <Box sx={detailsValueCellStyle}>
                                {isSectionLoading ? (
                                    getLoadingBox()
                                ) : canEditAdHocTaskMasterData() ? (
                                    <PlanInputFieldWrapper hasAccess={platformFeatures.signOff} hoverText={translateString("upgradePrivacyPlanToday")}>
                                        <DotLegalSelect
                                            options={[
                                                {
                                                    id: ApprovalOption.ApprovalNotRequired.toString(),
                                                    name: translateString("approvalNotRequired"),
                                                },
                                                {
                                                    id: ApprovalOption.ApprovalNotRequiredResponsibleGetNotified.toString(),
                                                    name: translateString("approvalNotRequiredResponsibleGetNotified"),
                                                },
                                                {
                                                    id: ApprovalOption.ApprovalRequiredByResponsible.toString(),
                                                    name: translateString("approvalRequiredByResponsible"),
                                                },
                                            ]}
                                            isLoading={isSectionLoading}
                                            selectedItem={data?.approval.toString()}
                                            placeholder={""}
                                            label={""}
                                            onChange={async (option) => masterDataUpdater.onApprovalChange(option!)}
                                            noMargin
                                            noOptionsLabel={translateString("noOptions")}
                                            disableClearable
                                            disableSearch
                                            disabled={!platformFeatures.signOff || isNullOrWhitespace(data?.responsible)}
                                            icon={!platformFeatures.signOff ? PurpleLockIcon : undefined}
                                            variant="standard"
                                        />
                                    </PlanInputFieldWrapper>
                                ) : (
                                    <DotLegalHeader marginBottom={0} headerStyle={"small"} fontWeight={400}>
                                        {translateString(setFirstLetterToLowerCase(ApprovalOption[data?.approval!].toString()))}
                                    </DotLegalHeader>
                                )}
                            </Box>
                        </React.Fragment>

                        <React.Fragment>
                            <Box sx={detailsLabelCellStyle}>
                                <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                    {translateString("documentation")}
                                </DotLegalHeader>
                            </Box>
                            <Box sx={detailsValueCellStyle}>
                                {isSectionLoading ? (
                                    getLoadingBox()
                                ) : canEditAdHocTaskMasterData() ? (
                                    <SelectWrapper>
                                        <DotLegalSelect
                                            options={[
                                                { id: "1", name: translateString("documentationIsRequired") },
                                                { id: "0", name: translateString("documentationIsNotRequired") },
                                            ]}
                                            onChange={async (value) => masterDataUpdater.onDocumentationRequiredChange(value)}
                                            label={translateString("documentation")}
                                            placeholder={translateString("documentation")}
                                            noOptionsLabel={""}
                                            noMargin
                                            disableClearable
                                            variant="standard"
                                            disableSearch
                                            selectedItem={data?.documentationRequired ? "1" : "0"}
                                        />
                                    </SelectWrapper>
                                ) : (
                                    <DotLegalHeader marginBottom={0} headerStyle={"small"} fontWeight={400}>
                                        {props.masterData?.documentationRequired
                                            ? translateString("documentationIsRequired")
                                            : translateString("documentationIsNotRequired")}
                                    </DotLegalHeader>
                                )}
                            </Box>
                        </React.Fragment>
                        {data && data.contract && (
                            <React.Fragment>
                                <Box sx={detailsLabelCellStyle}>
                                    <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                        {translateString("contract")}
                                    </DotLegalHeader>
                                </Box>
                                <Box sx={detailsValueCellStyle}>
                                    {isSectionLoading ? (
                                        getLoadingBox()
                                    ) : (
                                        <Box>
                                            {getAccordion(
                                                <>
                                                    <DotLegalLink bold to={urlProvider.getContractOverviewUrl(data.contract.contractId)} linkColor={"primary"}>
                                                        {data.contract.contractName}
                                                    </DotLegalLink>
                                                    <Box sx={{ whiteSpace: "nowrap" }}>
                                                        <Box
                                                            sx={(theme) => ({
                                                                fontSize: detailsValueFontSize,
                                                                color: theme.palette.primary.dark,
                                                            })}
                                                        >
                                                            {data.contract.contractParties.length === 1
                                                                ? translateString("oneContractParty")
                                                                : translateString("numberOfContractParties", {
                                                                      numberOfContractParties: data.contract.contractParties.length,
                                                                  })}
                                                        </Box>
                                                    </Box>
                                                </>,
                                                getContractPartiesTooltip(data.contract.contractParties),
                                                data.contract.contractParties.length === 0
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </React.Fragment>
                        )}
                        {props.onOpenActivity && (
                            <React.Fragment>
                                <Box sx={detailsLabelCellStyle}>
                                    <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                        {translateString("activity")}
                                    </DotLegalHeader>
                                </Box>
                                <Box sx={detailsValueCellStyle}>
                                    <Box sx={{ fontSize: detailsValueFontSize }}>
                                        <DotLegalLink bold onClick={props.onOpenActivity} linkColor={"primary"}>
                                            {translateString("seeActivity")}
                                        </DotLegalLink>
                                    </Box>
                                </Box>
                            </React.Fragment>
                        )}
                    </Box>
                </Box>
            </DotLegalCollapse>
        </>
    );
}

export default MasterDataSection;
